import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGetCallsQuery } from '@/store/api';
import { EmptyState, ErrorState, Panel, SkeletonRows, Status } from '@/components/ui';
import { formatDateTime, formatDuration, formatPhone } from '@/utils/format';

const FILTERS = [
  { label: 'All', value: '' },
  { label: 'Became orders', value: 'hasOrder' },
  { label: 'Passed to staff', value: 'transferred' },
  { label: 'Hung up', value: 'ABANDONED' }
] as const;

export function CallsPage() {
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const params: Record<string, string | number> = { page, limit: 25 };
  if (filter === 'hasOrder') params.hasOrder = 'true';
  else if (filter === 'transferred') params.transferred = 'true';
  else if (filter) params.status = filter;

  const { data, isLoading, isError, refetch } = useGetCallsQuery(params);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-4 text-display">Calls</h1>
        {FILTERS.map((option) => (
          <button
            key={option.label}
            onClick={() => {
              setFilter(option.value);
              setPage(1);
            }}
            className={`rounded-sm border px-2.5 py-1 text-small transition-colors ${
              filter === option.value
                ? 'border-ink bg-ink text-white'
                : 'border-rule bg-white text-slate hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <Panel>
        {isError ? (
          <ErrorState message="Could not load calls." onRetry={refetch} />
        ) : isLoading ? (
          <SkeletonRows rows={8} />
        ) : data?.data.length === 0 ? (
          <EmptyState
            headline="No calls match this filter"
            hint="Try a different filter, or check back after your next dinner service."
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-rule text-left text-micro text-slate">
                <th className="px-4 py-2 font-medium">Time</th>
                <th className="px-4 py-2 font-medium">Caller</th>
                <th className="px-4 py-2 font-medium">What they wanted</th>
                <th className="px-4 py-2 font-medium">Length</th>
                <th className="px-4 py-2 font-medium">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rule">
              {data?.data.map((call) => (
                <tr key={call._id} className="hover:bg-paper">
                  <td className="px-4 py-3">
                    <Link to={`/calls/${call._id}`} className="tabular text-small text-ink">
                      {formatDateTime(call.startedAt)}
                    </Link>
                  </td>
                  <td className="tabular px-4 py-3 text-small text-ink">
                    {formatPhone(call.callerNumber)}
                  </td>
                  <td className="px-4 py-3 text-small text-slate">
                    {call.intentHistory?.[0]?.intent
                      ? call.intentHistory[0].intent.replace(/_/g, ' ').toLowerCase()
                      : '—'}
                  </td>
                  <td className="tabular px-4 py-3 text-small text-slate">
                    {formatDuration(call.durationSeconds)}
                  </td>
                  <td className="px-4 py-3">
                    <Status value={call.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {data && data.meta.pages > 1 ? (
        <div className="flex items-center justify-between text-small text-slate">
          <span className="tabular">
            Page {data.meta.page} of {data.meta.pages}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded border border-rule bg-white px-2.5 py-1 disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </button>
            <button
              className="rounded border border-rule bg-white px-2.5 py-1 disabled:opacity-50"
              disabled={page >= data.meta.pages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
